/**
 * App del frontend-superadmin.
 *
 * Sprint 11 v2 S11-V2-11. Rutas:
 *   /login                          → PublicLayout + Login
 *   /auth/callback                  → PublicLayout + AuthCallback
 *   /sin-permiso                    → PublicLayout + SinPermiso
 *   /                               → AuthLayout + HomePage (protected)
 *   /configuracion-notificaciones   → AuthLayout + ConfiguracionNotificaciones (protected)
 *
 * Las rutas protegidas exigen rol super_admin.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './lib/auth/ProtectedRoute';
import { AuthLayout } from './components/layout/AuthLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { SinPermiso } from './pages/SinPermiso';
import { ConfiguracionNotificacionesPage } from './pages/ConfiguracionNotificaciones';
import { Dashboard } from './pages/Dashboard';
import { CatalogoSistemaPage } from './pages/superadmin/CatalogoSistema';
import { CargasRecursosPage } from './pages/superadmin/CargasRecursos';
import { CategoriasPage } from './pages/superadmin/Categorias';
import { ConfiguracionCuotaPage } from './pages/superadmin/ConfiguracionCuota';
import { DeploysPage } from './pages/superadmin/Deploys';
import { EntesExternosPage } from './pages/superadmin/EntesExternos';
import { LotesPage } from './pages/superadmin/Lotes';
import { PagosInicialesPage } from './pages/superadmin/PagosIniciales';
import { SaldoAperturaPage } from './pages/superadmin/SaldoApertura';
import { PersonasPage } from './pages/superadmin/Personas';
import { ProyectosPage } from './pages/superadmin/Proyectos';
import { RolesPage } from './pages/superadmin/Roles';
import { TenenciasSaPage } from './pages/superadmin/Tenencias';
// Preview dev-only del mantenedor de eWeLink (F4).
import { EwelinkPreview } from './pages/dev/EwelinkPreview';
import { EwelinkPage } from './pages/superadmin/Ewelink';

export default function App() {
  return (
    <Routes>
      {/* Previews dev-only: gateadas por import.meta.env.DEV, asi que Vite
          las elimina del bundle de produccion (convencion #15). */}
      {import.meta.env.DEV && (
        <Route path="/dev/ewelink" element={<EwelinkPreview />} />
      )}

      <Route element={<PublicLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/sin-permiso" element={<SinPermiso />} />
      </Route>

      <Route element={<ProtectedRoute requireRole="super_admin" />}>
        <Route element={<AuthLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route
            path="/configuracion-notificaciones"
            element={<ConfiguracionNotificacionesPage />}
          />
          <Route path="/superadmin/roles" element={<RolesPage />} />
          <Route path="/superadmin/catalogo" element={<CatalogoSistemaPage />} />
          {/* Sprint 13 S13-27: Sectores + Tipos de recurso se centralizaron
              en /superadmin/catalogo. Mantenemos las rutas viejas
              redirigiendo para no romper enlaces guardados. */}
          <Route
            path="/superadmin/sectores"
            element={<Navigate to="/superadmin/catalogo" replace />}
          />
          <Route
            path="/superadmin/tipos-recurso"
            element={<Navigate to="/superadmin/catalogo" replace />}
          />
          <Route path="/superadmin/lotes" element={<LotesPage />} />
          <Route path="/superadmin/personas" element={<PersonasPage />} />
          <Route path="/superadmin/tenencias" element={<TenenciasSaPage />} />
          <Route
            path="/superadmin/configuracion-cuota"
            element={<ConfiguracionCuotaPage />}
          />
          <Route path="/superadmin/categorias" element={<CategoriasPage />} />
          <Route
            path="/superadmin/pagos-iniciales"
            element={<PagosInicialesPage />}
          />
          <Route
            path="/superadmin/cargas-recursos"
            element={<CargasRecursosPage />}
          />
          <Route
            path="/superadmin/saldo-apertura"
            element={<SaldoAperturaPage />}
          />
          <Route path="/superadmin/proyectos" element={<ProyectosPage />} />
          <Route
            path="/superadmin/entes-externos"
            element={<EntesExternosPage />}
          />
          {/* Sprint 16 S16-11: mantenedor read-only del versionado por
              módulo funcional (auditoría de deploys). */}
          <Route path="/superadmin/deploys" element={<DeploysPage />} />
          {/* Integración eWeLink: qué dispositivos puede operar el portal. */}
          <Route path="/superadmin/ewelink" element={<EwelinkPage />} />
        </Route>
      </Route>

      {/*
        Sprint 13 S13-04/05 migraron /tesorero/pagos y
        /tesorero/compensaciones al portal bajo Vista Tesoreria.
        sa.calbucura.cl ya no aloja UIs operativas — solo
        mantencion (sectores, roles, tenencias, etc.) y
        configuracion (notificaciones).
      */}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
